import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import SaveButton from '../psc/lib/SaveButton'
import ErrorMessage from '../psc/lib/ErrorMessage'
import CancelButton from '../psc/lib/CancelButton'
import DeleteButton from '../psc/lib/DeleteButton'
import FilePaths, { FileButtons } from '../psc/lib/FilePaths'
import UserSelect from '../psc/lib/UserSelect'
import SingleSelect from '../psc/lib/SingleSelect'
import HasTemplate from '../psc/lib/HasTemplate'
import NoAccess from '../psc/lib/NoAccess'
import TextArea from '../psc/lib/TextArea'
import PickDate from '../psc/lib/PickDate'
import Title from '../psc/lib/Title'
import DocumentVersion from '../psc/lib/DocumentVersion'
import Classification from '../psc/lib/Classification'
import Status from '../psc/lib/Status'
import HeaderStatusInfo from '../psc/lib/HeaderStatusInfo'
import Loading from '../psc/lib/Loading'
import DocumentVersions from '../psc/lib/DocumentVersions';

import useAuthentication from '../../hooks/useAuthentication';
import useUserManagement from '../../hooks/useUserManagement'
import useQualityEntity, { ENTITY_TYPES } from '../../hooks/useQualityEntity'
import useGncHistory from '../../hooks/useGncHistory';
import useClassifications from '../../hooks/useClassifications';
import useBrowserTabTitle from '../../hooks/useBrowserTabTitle';

// Form field requirements for disabled and required statuses
const FORM_FIELDS = {
  area: {
    disabled: {
      notNew: true,
    },
    required: {
      always: true,
    },
  },
  type: {
    disabled: {
      notNew: true,
    },
    required: {
      always: true,
    },
  },
  isoSection: {
    disabled: {
      notNew: true,
    },
    required: {
      always: true,
    },
  },
  reference: {
    disabled: {
      always: true,
    },
  },
  title: {
    disabled: {
      originalStatusNotReserved: true,
      notMember: true,
    },
    required: {
      always: true,
    },
  },
  classification: {
    disabled: {
      originalStatusNotReserved: true,
      notMember: true,
    },
  },
  note: {
    disabled: {
      notMember: true,
    },
  },
  owner: {
    disabled: {
      originalStatusNotReserved: true,
      notMember: true,
    },
    required: {
      always: true,
    },
  },
  date: {
    disabled: {
      originalStatusNotReserved: true,
      notMember: true,
    },
    required: {
      live: true,
    },
  },
  status: {
    disabled: {
      notAdmin: true,
    },
  },
  files: {
    disabled: {
      notAdmin: true,
    },
    required: {
      live: true,
    },
  },
  template: {
    disabled: {
      statusNotReserved: true,
      notMember: true,
    },
  },
}

const QualityEntityForm = ({
  entityType,
  areaOptions,
  typeOptions,
  statusOptions,

  //Flags
  hasReference,
  hasClassification,
  
  // Callback functions
  onUpdateDocument,
  onForceReloadDocuments = () => {},
  onDelete,
}) => {
  const params = useParams()
  const isNew = params.objID === 'new'
  const objID = isNew ? params.objID : Number(params.objID)

  const pscState = useSelector(state => state.pscState.value)

  const { currentUser } = useAuthentication();

  const {
    goBack,
    replaceCurrentPath,
  } = useGncHistory()

  const { setBrowserTabTitle } = useBrowserTabTitle()

  const navigate = useNavigate()

  const {
      classifications,
  } = useClassifications()

  const hasClassificationOptions = useMemo(() =>
    hasClassification && classifications.filter(option => !option.is_archived ).length > 0
  , [hasClassification, classifications])

  const initialStatus = statusOptions[0]

  const config = useMemo(() => {
    switch (entityType.id) {
      case ENTITY_TYPES.QMS_DOCUMENT.id:
        return {
            hasAreaField: !!Number(pscState.config?.qms_documents_has_area_field),
            hasIsoSectionNumber: !!Number(pscState.config?.qms_documents_has_iso_section_number),
            canIssue: Number(pscState.config?.can_issue_qms_documents ?? 0),
            canRevision: Number(pscState.config?.can_revision_qms_documents ?? 0),
            initialRevision: pscState.config?.qms_documents_initial_revision ?? '',
            showLatest: true,
        }
      case ENTITY_TYPES.QA_DOCUMENT.id:
        return {
            hasAreaField: !!Number(pscState.config?.qa_documents_has_area_field),
            hasIsoSectionNumber: !!Number(pscState.config?.qa_documents_has_iso_section_number),
            canIssue: Number(pscState.config?.can_issue_qa_documents ?? 0),
            canRevision: Number(pscState.config?.can_revision_qa_documents ?? 0),
            initialRevision: pscState.config?.qa_documents_initial_revision ?? '',
            showLatest: true,
        }
      case ENTITY_TYPES.QA_RECORD.id:
      default:
        return {
            showLatest: false,
        }
    }
  }, [pscState.config])

  const {
    hasAreaField,
    hasIsoSectionNumber,
    canIssue,
    canRevision,
    initialRevision,
    showLatest,
  } = config

  const [area, setArea] = useState({ name: '' })
  const [type, setType] = useState({ name: '' })
  const [isoSectionNumber, setIsoSectionNumber] = useState(0)
  const [reference, setReference] = useState('')
  const [referenceWithoutVersion, setReferenceWithoutVersion] = useState('')
  const [issue, setIssue] = useState('01')
  const [issueNumber, setIssueNumber] = useState(1)
  const [revision, setRevision] = useState(initialRevision)
  const [title, setTitle] = useState('')
  const [classification, setClassification] = useState({ id: null, name: '', description: '' }) // For smart templates, populate fields with '' if there's no classification
  const [note, setNote] = useState('')
  const [ownerId, setOwnerId] = useState(null)
  const [date, setDate] = useState(null)
  const [originalStatus, setOriginalStatus] = useState(initialStatus)
  const [status, setStatus] = useState(initialStatus)
  const [filePaths, setFilePaths] = useState([])
  const [finalVersionFilePath, setFinalVersionFilePath] = useState('')
  const [characteristics, setCharacteristics] = useState({})
  const [isUsed, setIsUsed] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [downloadTemplateChecked, setDownloadTemplateChecked] = useState(true)
  const [showTemplateModal, setShowTemplateModal ] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUpissuing, setIsUpissuing] = useState(false)
  const [isUprevisioning, setIsUprevisioning] = useState(false)
  const [traceability, setTraceability] = useState(null);
  const [traceabilityLoading, setTraceabilityLoading] = useState(false);

  const [errors, setErrors] = useState([])

  const { 
    hasLaterNotWithdrawn,
    hasLaterLiveOrSuperseded,
  } = characteristics

  const { entityFormData, labels, roles, isLoading, noAccess } = useQualityEntity({
    entityType,
    id: objID,
    areaOptions,
    typeOptions,
    statusOptions,
    isNew,
  })

  const {
    areaLabel,
    typeLabel,
    referenceLabel,
    autoCreatedInfoText,
    isoSectionLabel,
    titleLabel,
    classificationLabel,
    noteLabel,
    ownerLabel,
    dateLabel,
    noteInfoText,
    filesLabel,
  } = labels

  const {
    adminRole,
    userRole,
    readOnlyRole,
  } = roles

  const { userHasRole, getUserFromID } = useUserManagement()
  const userIsAdmin = useMemo(() => userHasRole(currentUser, adminRole), [currentUser, adminRole, userHasRole])
  const userIsMember = useMemo(() => userHasRole(currentUser, userRole), [currentUser, userRole, userHasRole])

  const templateData = useMemo(() => {
    // FOR QA_RECORD only type, references, title, note and owner are used
    return {
      area: { name: area?.name },
      [entityType.fields.type]: { name: type?.name },
      iso_section_number: isoSectionNumber,
      reference,
      reference_without_version: referenceWithoutVersion,
      issue,
      revision,
      title,
      classification,
      note,
      owner_user_id: ownerId,
      [entityType.fields.date]: date,
    }
  }, [
    entityType, classification, issue, date, note, ownerId, reference, referenceWithoutVersion,
    revision, title, area?.name, isoSectionNumber,
  ])

  // As per PSC_Status_Logic_v4.docx
  const statuses = useMemo(() =>
    statusOptions.map(statusData => {
      if (hasLaterLiveOrSuperseded) {
        return {
          ...statusData,
          disabled: !['Withdrawn', 'Superseded'].includes(statusData.name),
        }
      }

      if (hasLaterNotWithdrawn) {
          return {
            ...statusData,
            disabled: !['Withdrawn', 'Live', 'Filed'].includes(statusData.name),
          }
      }

      return {
        ...statusData,
        disabled: ['Superseded'].includes(statusData.name),
      }
    }
  ), [statusOptions, characteristics])

  const fetchReference = useCallback(async () => {
      // For QMS and QA Documents, reference depends on type, area, ISO section number, issue number and revision
      // For QA Records, reference depends only on type
      const hasRequired = entityType.id === ENTITY_TYPES.QMS_DOCUMENT.id || entityType.id === ENTITY_TYPES.QA_DOCUMENT.id
        ? !!type.id || !!area?.id || !!isoSectionNumber
        : !!type.id // QA Record

      if (!hasRequired) {
        setReference('')
        setReferenceWithoutVersion('')
        return
      }

      const body = entityType.id === ENTITY_TYPES.QA_RECORD.id ? {
              qa_record_type_id: type.id,
          } : {
            [entityType.fields.typeId]: type.id ?? 0,
            [entityType.fields.areaId]: area?.id ?? 0,
            iso_section_number: isoSectionNumber,
            issue_number: issueNumber,
            issue,
            revision,
          }

      const response = await fetch(`/v0/${entityType.id}/reference`, {
          credentials: 'same-origin',
          headers: {
              'Content-Type': 'application/json'
          },
          method: 'POST',
          body: JSON.stringify(body)
      })

      const json = await response.json()

      const { result } = json

      setReference(result.reference)
      setReferenceWithoutVersion(result.reference_without_version)
  }, [entityType, type?.id, area?.id, isoSectionNumber, issueNumber, issue, revision])

  useEffect(() => {
    if (isNew) {
        if (ownerId === null) {
            setOwnerId(currentUser?.id)
        }
        if (entityType.id === ENTITY_TYPES.QA_RECORD.id) {
            setDate(new Date());
        }
        if (entityType.kind === 'Document' && filePaths.length === 0) {
            setFilePaths([''])
        }
        fetchReference()
        setBrowserTabTitle(`New ${entityType.name}`)
        return
    }

    if (entityFormData) {
      const {
        area,
        type,
        isoSectionNumber,
        reference,
        referenceWithoutVersion,
        issue,
        issueNumber,
        revision,
        title,
        classification,
        note,
        ownerId,
        date,
        status,
        filePaths,
        finalVersionFilePath,
        characteristics,
        isUsed,
        updatedAt,
      } = entityFormData

      setArea(area)
      setType(type)
      setIsoSectionNumber(isoSectionNumber)
      setReference(reference)
      setReferenceWithoutVersion(referenceWithoutVersion)
      setIssue(issue)
      setIssueNumber(issueNumber)
      setRevision(revision)
      setTitle(title)
      setClassification(classification || { id: null, name: '', description: '' })
      setNote(note)
      setOwnerId(ownerId)
      setDate(date)
      setOriginalStatus(status)
      setStatus(status)
      setFilePaths(filePaths)
      setFinalVersionFilePath(finalVersionFilePath)
      setCharacteristics(characteristics)
      setIsUsed(isUsed)
      setUpdatedAt(updatedAt)

      if (entityType.id !== ENTITY_TYPES.QA_RECORD.id) {
        setDownloadTemplateChecked(issueNumber === 1 && !revision)
      }

      setBrowserTabTitle(`${reference} ${title}`)
    }

  }, [isNew, currentUser.id, objID, entityFormData, fetchReference])

  useEffect(() => {
    if( isNew ) {
      return;
    }
    setTraceabilityLoading(true);
    fetch('/v0/' + entityType.id + '/' + objID + '/traceability', { credentials: 'same-origin' })
      .then(function(response) {
        if (!response.ok) throw response;
        return response.json();
      })
      .then(function(json) {
        console.log("Get Traceability", json.result)
        setTraceability(json.result);
        setTraceabilityLoading(false);
      })
      .catch(error => {
        console.log("Get Traceability error", error);
        setTraceabilityLoading(false);
      });
  }, [objID, isNew]);
  
  const disabledFields = useMemo(() => Object.fromEntries(
    Object.entries(FORM_FIELDS).map(([field, { disabled }]) => {
      if (disabled?.notNew && !isNew) {
        return [field, true]
      }

      if (disabled?.notMember && !userIsMember) {
        return [field, true]
      }

      if (disabled?.notAdmin && !userIsAdmin) {
        return [field, true]
      }

      if (disabled?.originalStatusNotReserved && originalStatus?.name !== 'Reserved') {
        return [field, true]
      }

      if (disabled?.statusNotReserved && status?.name !== 'Reserved') {
        return [field, true]
      }

      return [field, disabled?.always]
    })
  ), [isNew, originalStatus?.name, status?.name, userIsMember, userIsAdmin])

  const displayClassification = useMemo(() =>
    hasClassificationOptions
  , [hasClassificationOptions])

  const displayDocumentVersions = useMemo(() => {
    if (isNew || entityType.id === ENTITY_TYPES.QA_RECORD.id) {
      return false
    }
    return true
  }, [isNew, entityType?.id])
  
  const { requiredFields, invalidFields } = useMemo(() => {
    // This is messed up, but it is technically possible status and type is not defined
    if ( !status || !type )
        return {
            requiredFields: [],
            invalidFields: [],
        }
    
    const requirementsForLive = (status.name === 'Live' || status.name === 'Filed') && userIsAdmin
    const requirementsForReserved = status.name === 'Reserved' && userIsMember
    const validationFieldsConfig = {
      area: {
        label: areaLabel,
        isValid: hasAreaField ? !!area?.id : true,
      },
      type: {
        label: typeLabel,
        isValid: !!type.id
      },
      isoSection: {
        label: isoSectionLabel,
        isValid: hasIsoSectionNumber ? typeof isoSectionNumber === 'number' : true
      },
      title: {
        label: titleLabel,
        isValid: !!title.trim()
      },
      classification: {
        label: classificationLabel,
        isValid: displayClassification ? !!classification.id : true
      },
      owner: {
        label: ownerLabel,
        isValid: !!ownerId
      },
      date: {
        label: dateLabel,
        isValid: !!date
      },
      files: {
        label: filesLabel,
        isValid: !!finalVersionFilePath
      },
    }

    const requiredFieldsConfig = Object.entries(FORM_FIELDS)
      .filter(([_, { required }]) => typeof required !== 'undefined')
      .map(([field, { required }]) => {
        if (required?.always) {
          return [field, true]
        }

        if (required?.live && requirementsForLive) {
          return [field, true]
        }

        if (required?.reserved && requirementsForReserved) {
          return [field, true]
        }

        return [field, false]
      })

    if (requiredFieldsConfig.length !== Object.keys(validationFieldsConfig).length) {
      console.error('Validation mismatch for required fields')
    }

    const invalid = requiredFieldsConfig
      .filter(([_, isRequired]) => isRequired)
      .map(([field]) => ([field, validationFieldsConfig[field] ?? {}]))
      .filter(([_, { isValid }]) => isValid === false)

    return {
      requiredFields: Object.fromEntries(requiredFieldsConfig),
      invalidFields: Object.fromEntries(invalid),
    }
  }, [
    area?.id, type?.id, isoSectionNumber, title, classification, ownerId, date, finalVersionFilePath,
    status?.name, userIsMember, userIsAdmin, hasAreaField, hasIsoSectionNumber, displayClassification,
  ])

  const requestBody = useMemo(() => {
    const shouldClearFilePaths = ( status?.name === 'Reserved' || status?.name === 'Withdrawn' )

    // Base data for all entity types
    const baseData = {
      [entityType.fields.typeId]: type?.id,
      [entityType.fields.statusId]: status?.id,
      [entityType.fields.date]: date,
      owner_user_id: ownerId,
      updated_at: updatedAt,
      reference,
      title,
      note,
      // Hack hack hack.. If status == Reserved || Withdrawn, clear out all file paths
      file_paths: shouldClearFilePaths ? [] : filePaths,
      final_version_file_path: shouldClearFilePaths ? '' : finalVersionFilePath,
    }

    if (entityType.id === ENTITY_TYPES.QA_RECORD.id) {
      return baseData
    }

    // Data for QMS and QA documents
    return {
      ...baseData,
      [entityType.fields.areaId]: area?.id,
      iso_section_number: isoSectionNumber,
      reference_without_version: referenceWithoutVersion,
      issue_number: issueNumber,
      issue,
      revision,
      classification_id: classification.id,
      classification,
    }
  }, [
    entityType, type?.id, status?.id, date, ownerId, updatedAt, reference, title, note, filePaths, finalVersionFilePath,
    classification, area?.id, isoSectionNumber, referenceWithoutVersion, issueNumber, issue, revision,
  ])

  const handleAreaChange = ({ id }) => {
    const area = areaOptions?.find(item => item.id === id)
    setArea(area)
  }

  const handleTypeChange = ({ id }) => {
    const type = typeOptions.find(item => item.id === id)
    setType(type)
  }

  const handleStatusChange = (id) => {
    const status = statusOptions.find(item => item.id === id)
    setStatus(status)
  }

  const handleSubmitError = ({ message }) => {
    setIsSubmitting(false)
    setIsUpissuing(false)
    setIsUprevisioning(false)
    setErrors([ message ])
  }

  const handleSubmit = (event) => {
    event?.preventDefault()

    if (isSubmitting) {
      return null
    }

    const validationErrors = Object.entries(invalidFields)
      .map(([_, { label }]) => `${label} is required`)

    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return null
    }

    setIsSubmitting(true)

    const shouldShowTemplate = !disabledFields.template && downloadTemplateChecked && type?.template?.id > 0

    return {
      objType: entityType.id,
      objID,
      data: requestBody,
      navigateOverride: shouldShowTemplate ? () => {} : null,
      successCallback: (json) => {
        setIsSubmitting(false)
        onUpdateDocument(json.result)
        onForceReloadDocuments()

        if (shouldShowTemplate) {
          setShowTemplateModal(true)
        }
      },
    }
  }

  const handleUpIssueAndUpRevision = (upType) => {
    if (isSubmitting) {
      return null
    }

    if (upType === 'upissue') {
      setIsUpissuing(true)
    }

    if (upType === 'uprevision') {
      setIsUprevisioning(true)
    }

    return {
      objType: entityType.id,
      objID,
      data: requestBody,
      url: `/v0/${entityType.id}/${objID}/${upType}`,
      successCallback: () => {
        setIsSubmitting(false)
        onForceReloadDocuments()
        replaceCurrentPath(`/${entityType.id}`)
      },
      navigateOverride: ({ result }) => {
          navigate(`/${entityType.id}/${result.id}`)
      }
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (noAccess) {
    return (<NoAccess />)
  }

  return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                          <div style={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap'}}>
                            {!isNew ?
                                <HeaderStatusInfo
                                    type={entityType.kind}
                                    id={objID}
                                    status={originalStatus?.name}
                                />
                            : null}

                            {displayDocumentVersions &&
                              <DocumentVersions 
                                  isHidden={isNew}
                                  reference={reference}
                                  title={title}
                                  documentStatusName={status?.name}
                                  traceability={traceability}
                                  traceabilityLoading={traceabilityLoading}
                              />
                            }
                          </div>
                          
                            <Title
                                type={entityType.name}
                                reference={reference}
                                title={title}
                                id={objID}
                                showLatest={showLatest}
                            />

                            <FileButtons
                                finalVersionFilePath={finalVersionFilePath}
                                buttonClass={originalStatus?.name === 'Superseded' ? 'btn-warning' : ''}
                                visible={originalStatus?.name === 'Superseded' || originalStatus?.name === 'Live' || originalStatus?.name === 'Filed'}
                            />
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={handleSubmit}>
                                {hasAreaField
                                    ? (
                                        <div className="form-group">
                                            <label className="col-sm-2 control-label required">{areaLabel}</label>
                                            <div className="col-sm-8">
                                                <SingleSelect
                                                    value={area?.id}
                                                    onChange={handleAreaChange}
                                                    options={areaOptions}
                                                    disabled={disabledFields.area}
                                                />
                                            </div>
                                        </div>
                                    ) : null
                                }

                                <div className="form-group">
                                    <label className="col-sm-2 control-label required">{typeLabel}</label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={type?.id}
                                            onChange={handleTypeChange}
                                            options={isNew ? typeOptions.filter(item => !item.is_archived) : typeOptions}
                                            disabled={disabledFields.type}
                                        />
                                    </div>
                                </div>

                                {hasIsoSectionNumber ?
                                    <div className="form-group">
                                        <label className="col-sm-2 control-label required">{isoSectionLabel}</label>
                                        <div className="col-sm-8">
                                            <input className="form-control" type="number"
                                                min="0" max="10"
                                                value={isoSectionNumber}
                                                onChange={({ target: { value }}) => setIsoSectionNumber(Number(value))}
                                                required
                                                disabled={disabledFields.isoSection}
                                            />
                                        </div>
                                    </div> : null
                                }

                                {hasReference ?
                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">{referenceLabel}</label>
                                        <div className="col-sm-8">
                                            <input className="form-control" type="text" name="reference"
                                                value={reference}
                                                disabled
                                            />
                                            <span className="help-block m-b-none m-t-none">
                                                <small className="form-info-text" > {autoCreatedInfoText}</small>
                                            </span>
                                        </div>
                                    </div>
                                : null}

                                <DocumentVersion
                                    canIssue={canIssue}
                                    canRevision={canRevision}
                                    issue={issue}
                                    revision={revision}
                                />
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label required">{titleLabel}</label>
                                    <div className="col-sm-8">
                                        <input
                                          className="form-control"
                                          type="text"
                                          name="title"
                                          value={title}
                                          disabled={disabledFields.title}
                                          onChange={({ target: { value } }) => setTitle(value)}
                                        />
                                    </div>
                                </div>

                                {displayClassification ?
                                    <Classification
                                        isNew={isNew}
                                        value={classification.id}
                                        setClassification={setClassification}
                                        required={!disabledFields.classification}
                                        disabled={disabledFields.classification}
                                    />
                                : null}

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{noteLabel}</label>
                                    <div className="col-sm-8">
                                        <TextArea
                                            className="form-control"
                                            name="note"
                                            value={note}
                                            onChange={({ target: { value } }) => setNote(value)}
                                            disabled={disabledFields.note}
                                        />
                                        {noteInfoText ?
                                            <span className="help-block m-b-none m-t-none">
                                                <small className="form-info-text">{noteInfoText}</small>
                                            </span>
                                        : null}
                                    </div>
                                </div>

                                <UserSelect
                                    label={ownerLabel}
                                    value={ownerId}
                                    onChange={setOwnerId}
                                    required={requiredFields.owner}
                                    requiredRole={readOnlyRole}
                                    disabled={disabledFields.owner}
                                />

                                <Status
                                    value={status?.id}
                                    onChange={handleStatusChange}
                                    statuses={statuses}
                                    disabled={disabledFields.status}
                                />

                                <div className="form-group">
                                  <label className={`col-sm-2 control-label ${requiredFields.date ? 'required' : ''}`}>{dateLabel}</label>
                                  <div className="col-sm-8">
                                    <PickDate
                                        value={date}
                                        onChange={setDate}
                                        disabled={disabledFields.date}
                                    />
                                  </div>
                                </div>

                                <div className="hr-line-dashed" />

                                <FilePaths
                                    filePaths={filePaths}
                                    finalVersionFilePath={finalVersionFilePath}
                                    setFilePaths={setFilePaths}
                                    setFinalVersionFilePath={setFinalVersionFilePath}
                                    filePathsInUse={status?.name === 'Superseded' || status?.name === 'Live' || status?.name === 'Filed'}
                                    finalVersionFilePathInUse={status?.name === 'Superseded' || status?.name === 'Live' || status?.name === 'Filed'}
                                    showFileLabels={entityType.kind === 'Document'}
                                    showRequired={requiredFields.files}
                                    displayOriginal={entityType.kind === 'Document'}
                                    buttonClass={originalStatus?.name === "Superseded" ? "btn-warning" : ""}
                                    disabled={disabledFields.files}
                                />

                                {!disabledFields.template ?
                                    <>
                                        <div className="hr-line-dashed" />
                                        <HasTemplate
                                            templateData={() => templateData} /* TODO: This doesn't need to be a function */
                                            templateID={type?.template?.id}
                                            filename={`${reference} ${title}`}
                                            visible
                                            downloadOnSave={downloadTemplateChecked}
                                            onDownloadOnSaveChange={setDownloadTemplateChecked}
                                            showModal={showTemplateModal}
                                            onModalClose={goBack}
                                        />
                                    </>
                                : null}

                                <div className="hr-line-dashed" />

                                <div className="form-group">
                                    <ErrorMessage
                                        error={errors}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userIsMember}
                                            spinnerActive={isSubmitting}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        {!isNew && userIsAdmin && !isUsed &&
                                            <DeleteButton
                                                url={`/v0/${entityType.id}/${objID}`}
                                                onError={handleSubmitError}
                                                callback={() => onDelete(objID)}
                                            />
                                        }

                                        {!!canIssue
                                            && !hasLaterNotWithdrawn
                                            && userIsMember
                                            && originalStatus?.name === 'Live'
                                            && status?.name === 'Live'
                                            && (<SaveButton
                                                  buttonVariant='info'
                                                  visible
                                                  spinnerActive={isUpissuing}
                                                  label='Upissue'
                                                  onClick={() => handleUpIssueAndUpRevision('upissue')}
                                                  onError={handleSubmitError}
                                              />
                                          )}

                                        {!!canRevision
                                            && !hasLaterNotWithdrawn
                                            && userIsMember
                                            && originalStatus?.name === 'Live'
                                            && status?.name === 'Live'
                                            && (<SaveButton
                                                    buttonVariant='warning'
                                                    visible
                                                    spinnerActive={isUprevisioning}
                                                    label='Uprevision'
                                                    onClick={() => handleUpIssueAndUpRevision('uprevision')}
                                                    onError={handleSubmitError}
                                                />
                                            )
                                        }
                                    </div>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default QualityEntityForm
